import React, { useState } from 'react';
import { useTaskCompletionRate } from '../hooks';
import { formatDate } from '../../../config/appConfig';

export interface TaskCompletionRateProps {
  embedded?: boolean;
}

const cardClass = (embedded?: boolean) =>
  embedded ? 'min-w-0' : 'bg-white rounded-xl shadow-sm p-6 border border-gray-100';

const TaskCompletionRate: React.FC<TaskCompletionRateProps> = ({ embedded } = {}) => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  const getDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const { startDate, endDate } = getDateRange();
  const { data, isLoading } = useTaskCompletionRate(startDate, endDate);

  if (isLoading) {
    return (
      <div className={cardClass(embedded)}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Task Completion Rate</h2>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading data...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={cardClass(embedded)}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Task Completion Rate</h2>
        </div>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No task data available
        </div>
      </div>
    );
  }

  const { completionRate, total, completed, inProgress, pending, overdue, byStatus } = data;
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (completionRate / 100) * circumference;

  const statusColors: Record<string, string> = {
    completed: '#10b981',
    'in-progress': '#3b82f6',
    pending: '#f59e0b',
    overdue: '#ef4444',
  };

  return (
    <div className={cardClass(embedded)}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Task Completion Rate</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as any)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        >
          <option value="week">Last Week</option>
          <option value="month">Last Month</option>
          <option value="quarter">Last Quarter</option>
          <option value="year">Last Year</option>
        </select>
      </div>

      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* Gauge Chart */}
        <div className="flex-shrink-0">
          <div className="relative w-48 h-48">
            <svg className="transform -rotate-90" width="192" height="192">
              {/* Background circle */}
              <circle
                cx="96"
                cy="96"
                r={radius}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="16"
              />
              {/* Progress circle */}
              <circle
                cx="96"
                cy="96"
                r={radius}
                fill="none"
                stroke={completionRate >= 80 ? '#10b981' : completionRate >= 50 ? '#f59e0b' : '#ef4444'}
                strokeWidth="16"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">{completionRate.toFixed(1)}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">Total Tasks</div>
              <div className="text-2xl font-semibold text-gray-900">{total}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-sm text-green-600">Completed</div>
              <div className="text-2xl font-semibold text-green-700">{completed}</div>
            </div>
          </div>

          <div className="space-y-2">
            {byStatus && byStatus.length > 0 ? (
              byStatus.map((status) => (
                <div key={status.status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: statusColors[status.status.toLowerCase()] || '#6b7280',
                      }}
                    />
                    <span className="text-sm text-gray-700 capitalize">{status.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all duration-500"
                        style={{
                          width: `${status.percentage}%`,
                          backgroundColor: statusColors[status.status.toLowerCase()] || '#6b7280',
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {status.count}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm text-gray-700">Completed</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{completed}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm text-gray-700">In Progress</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{inProgress}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-sm text-gray-700">Pending</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{pending}</span>
                </div>
                {overdue > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-sm text-gray-700">Overdue</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{overdue}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCompletionRate;
